import { Team, FixtureOpponent, FixtureResult } from "../types/team";
import { getAllQualifiedTeams } from "./getAllQualifiedTeams";

const POTS: Array<1 | 2 | 3 | 4> = [1, 2, 3, 4];
const OPPONENTS_PER_POT = 2;
const MAX_ATTEMPTS = 100;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getCountryCounts(teams: Team[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const team of teams) {
    counts[team.country] = (counts[team.country] || 0) + 1;
  }
  return counts;
}

// Bir torbadan, ülke başına en fazla 2 kısıtına uyacak şekilde 2 rakip seçmeyi dener.
// Uygun 2 rakip bulunamazsa null döner (dead end -> çağıran yeniden dener).
function pickOpponentsFromPot(
  pot: 1 | 2 | 3 | 4,
  selectedTeam: Team,
  allTeams: Team[],
  alreadyPicked: Team[]
): Team[] | null {
  const candidates = allTeams.filter(
    (t) =>
      t.pot === pot &&
      t.name !== selectedTeam.name &&
      t.country !== selectedTeam.country
  );

  const picked: Team[] = [];
  for (const team of shuffle(candidates)) {
    const countryCounts = getCountryCounts([...alreadyPicked, ...picked, team]);
    if (countryCounts[team.country] <= 2) {
      picked.push(team);
      if (picked.length === OPPONENTS_PER_POT) break;
    }
  }

  return picked.length === OPPONENTS_PER_POT ? picked : null;
}

// Torba başına seçilen 2 rakipten birine rastgele iç saha, diğerine deplasman atar.
function assignHomeAway(potPicks: Team[][]): FixtureOpponent[] {
  const opponents: FixtureOpponent[] = [];
  for (const pair of potPicks) {
    const [home, away] = shuffle(pair);
    opponents.push({ ...home, isHome: true, matchday: 0 });
    opponents.push({ ...away, isHome: false, matchday: 0 });
  }
  return opponents;
}

function assignMatchdays(opponents: FixtureOpponent[]): FixtureOpponent[] {
  const matchdays = shuffle([1, 2, 3, 4, 5, 6, 7, 8]);
  return opponents.map((o, i) => ({ ...o, matchday: matchdays[i] }));
}

function attemptFixture(selectedTeam: Team, allTeams: Team[]): FixtureOpponent[] | null {
  const potPicks: Team[][] = [];
  let alreadyPicked: Team[] = [];

  for (const pot of POTS) {
    const picked = pickOpponentsFromPot(pot, selectedTeam, allTeams, alreadyPicked);
    if (!picked) return null;
    potPicks.push(picked);
    alreadyPicked = [...alreadyPicked, ...picked];
  }

  const opponents = assignHomeAway(potPicks);

  const homeCount = opponents.filter((o) => o.isHome).length;
  const awayCount = opponents.filter((o) => !o.isHome).length;
  if (opponents.length !== 8 || homeCount !== 4 || awayCount !== 4) return null;

  return assignMatchdays(opponents).sort((a, b) => a.matchday - b.matchday);
}

export function generateDirectTeamFixture(selectedTeam: Team): FixtureResult {
  const allTeams = getAllQualifiedTeams();

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const fixture = attemptFixture(selectedTeam, allTeams);
    if (fixture) {
      return {
        status: "direct",
        team: selectedTeam,
        pot: selectedTeam.pot,
        fixture
      };
    }
  }

  throw new Error(
    `${selectedTeam.name} için ${MAX_ATTEMPTS} denemede geçerli fikstür üretilemedi.`
  );
}
